import { isNullOrUndefined } from "@silo/utils";
import { Signer } from "ethers";
import { ReactNode, createContext, useContext } from "react";
import { useSiloWeb3 } from "./SiloWeb3Provider";
import {
  ChecksumAddress,
  ClientProvider,
  ProtocolInstance,
  SigningProvider,
  SupportedChainId,
  isSupportedChainId,
} from "@silo/types";
import { isUndefined } from "lodash";

export interface SiloWeb3User {
  clientProvider: ClientProvider;
  signingProvider: SigningProvider;
  signer: Signer;
  account: ChecksumAddress;
  chainId: SupportedChainId;
  protocolInstance: ProtocolInstance;
}

const SiloWeb3UserContext = createContext<SiloWeb3User>(null!);

export const useSiloWeb3User = () => {
  const context = useContext(SiloWeb3UserContext);

  if (isNullOrUndefined(context)) {
    throw new Error(
      "useSiloWeb3User must be used within a SiloWeb3UserBoundary"
    );
  }

  return context;
};
interface SiloWeb3UserBoundaryProps {
  fallback?: ReactNode;
  children?: ReactNode;
}

/**
 * Only renders children if there is an active web3 user.
 *
 * Use the `useSiloWeb3User` hook to access the user.
 */
export function SiloWeb3UserBoundary({
  fallback = null,
  children,
}: SiloWeb3UserBoundaryProps): JSX.Element | null {
  const {
    clientProvider,
    signingProvider,
    account,
    chainId,
    protocolInstance,
  } = useSiloWeb3();

  if (
    isNullOrUndefined(clientProvider) ||
    isNullOrUndefined(signingProvider) ||
    isNullOrUndefined(account) ||
    !isSupportedChainId(chainId) ||
    isUndefined(protocolInstance)
  ) {
    return fallback ? <>{fallback}</> : null;
  }

  return (
    <SiloWeb3UserContext.Provider
      value={{
        clientProvider: clientProvider,
        signingProvider,
        account,
        chainId,
        protocolInstance,
        signer: signingProvider.getSigner(account),
      }}
    >
      {children}
    </SiloWeb3UserContext.Provider>
  );
}
